import React, { useEffect, useState } from 'react';
import { NWClient } from '../../../client/NWClient';
import { AITemplateModel } from '../../../models/ai.template.model';
import { toast } from 'react-toastify';
import { DocumentTemplateModel } from '../../../models/document.template.model';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { ROUTES } from '../../../common/constants/routes';
import { useNavigate, useSearchParams } from 'react-router-dom';

export const useTemplateCreation = (
  id: number,
  isGenerated: boolean,
  documentUploadHTML?: number
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const generateTemplate = params.get('generateTemplate');
  const currentUser = useAppSelector(selectUser);
  const [templateCreationEnabled, setTemplateCreationEnabled] = useState<boolean>(false);
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);
  const [planUpgradingMessage, setPlanUpgradingMessage] = useState<boolean>(false);
  const [documentTemplate, setDocumentTemplate] = useState<DocumentTemplateModel>(null);
  const [template, setTemplate] = useState<
    {
      id?: number;
      data: { field: string; value: string; placeholder: string };
    }[]
  >([]);
  const docProp = () => {
    if (isGenerated) {
      return 'document_contract_id';
    } else {
      /*if (checkedLatestVersion) {
        return 'document_upload_html';
      } else {*/
      return 'document_upload_id';
      /*}*/
    }
  };
  const creatingTemplateInitial = () => {
    setPendingStatus(true);
    NWClient.post('ai-template', {
      [docProp()]: /*checkedLatestVersion ? documentUploadHTML : */ id,
    })
      .then((res: AITemplateModel[]) => {
        localStorage.setItem('AITemplateFail', 'false');
        setTemplate(
          res.map((el) => ({
            id: el.id,
            data: { ...el.data },
          }))
        );
      })
      .catch((error) => {
        setTemplateCreationEnabled(false);
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          setPlanUpgradingMessage(true);
        } else {
          if (localStorage.getItem('AITemplateFail') === 'true') {
            toast.error(error.message ? error.message : t('messages.errorOccurred'));
          } else {
            localStorage.setItem('AITemplateFail', 'true');
            toast.error(t('messages.firstAITemplateFail'));
          }
        }
      })
      .finally(() => {
        setPendingStatus(false);
      });
  };
  const creatingTemplate = () => {
    setPendingStatus(true);
    NWClient.post('document-template', {
      document_upload: id,
      user: currentUser.id,
    })
      .then((res: DocumentTemplateModel) => {
        toast.success(t('messages.templateCreated'));
        setDocumentTemplate(res);
        navigatingToCreatedTemplate(res.id);
      })
      .catch((error) => {
        setPendingStatus(false);
        if (
          error.response?.data?.errors?.length > 0 &&
          error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
        ) {
          setPlanUpgradingMessage(true);
        } else {
          toast.error(error.message ? error.message : t('messages.errorOccurred'));
        }
      });
  };
  const navigatingToCreatedTemplate = (id: number) => {
    navigate(`${ROUTES.DOCUMENT_LIBRARY}/company-templates/${id}?templateViewEditMode=true`);
  };
  const deletePlaceholder = (id?: number, index?: number) => {
    if (id) {
      setPendingStatus(true);
      NWClient.delete('document-template-placeholder', id, null, { user: currentUser.id })
        .then(() => {
          toast.success(t('messages.templatePlaceholderDeleted'));
          if (index !== undefined) {
            const templateCopy = template.slice();
            templateCopy.splice(index, 1);
            setTemplate(templateCopy);
          }
        })
        .catch((error) => {
          toast.error(error.message ? error.message : t('messages.errorOccurred'));
        })
        .finally(() => {
          setPendingStatus(false);
        });
    } else if (index) {
      const templateCopy = template.slice();
      templateCopy.splice(index, 1);
      setTemplate(templateCopy);
    }
  };
  const addTemplatePlaceholderRow = () => {
    const templateCopy = template.slice();
    const item = { data: { field: '', value: '', placeholder: '' } };
    templateCopy.push(item);
    setTemplate(templateCopy);
  };

  useEffect(() => {
    if (generateTemplate) {
      setTemplateCreationEnabled(true);
    }
  }, [generateTemplate]);
  useEffect(() => {
    if (templateCreationEnabled && id && documentUploadHTML) {
      creatingTemplateInitial();
    }
  }, [templateCreationEnabled, id, documentUploadHTML]);

  return {
    templateCreationEnabled,
    setTemplateCreationEnabled,
    pendingStatus,
    setPendingStatus,
    planUpgradingMessage,
    setPlanUpgradingMessage,
    template,
    setTemplate,
    creatingTemplate,
    documentTemplate,
    setDocumentTemplate,
    navigatingToCreatedTemplate,
    deletePlaceholder,
    addTemplatePlaceholderRow,
  };
};
